import { LegalPageShell } from "@/components/app/LegalPageShell";
import { usePageTitle } from "../hooks/usePageTitle";

export function TermsPage() {
  usePageTitle("Terms of Service | TidyRota");

  return (
    <LegalPageShell
      title="Terms of Service"
      lastUpdated="21 April 2026"
      summary={
        <>
          TidyRota is a tool that helps you schedule cleaners, verify they
          turned up, and see what happened on each shift. It costs &pound;29
          a month plus &pound;3.79 per cleaner, with a 14-day free trial.
          You can cancel at any time. We do our best to keep it running
          well, but it&rsquo;s provided &ldquo;as is&rdquo; with no
          availability guarantee. You&rsquo;re responsible for what you and
          your team do with it, including being open with your cleaners
          about how their location is tracked at check-in.
        </>
      }
    >
      <section>
        <h2>1. Who we are and what these Terms cover</h2>
        <p>
          TidyRota is a trading name of Jack Hodkinson, a sole trader
          registered in England &amp; Wales (&ldquo;we&rdquo;,
          &ldquo;us&rdquo;, &ldquo;our&rdquo;). These Terms are the
          agreement between us and the person or business using TidyRota
          (&ldquo;you&rdquo;). By creating an account, you accept them.
        </p>
        <p>
          Contact:{" "}
          <a href="mailto:hello@tidyrota.com">hello@tidyrota.com</a>
          <br />
          Post: TidyRota, 152 Leeds Road, Wakefield, WF1 2QA, United
          Kingdom.
        </p>
      </section>

      <section>
        <h2>2. The service</h2>
        <p>
          TidyRota is a web application that lets commercial cleaning
          companies:
        </p>
        <ul>
          <li>
            Manage cleaning sites and their location details.
          </li>
          <li>Schedule shifts and assign cleaners to them.</li>
          <li>
            Record cleaners&rsquo; arrival and departure, with a best-effort
            GPS location check.
          </li>
          <li>Review history (&ldquo;visits&rdquo;).</li>
          <li>Pay for the service through Stripe.</li>
        </ul>

        <h3>What TidyRota is not</h3>
        <p>TidyRota is a scheduling and operations tool. It is not:</p>
        <ul>
          <li>
            A payroll system, an official timekeeping record, or a
            tax-filing tool. Check-in timestamps are operational data,
            not a substitute for official time records.
          </li>
          <li>
            An employment-law compliance tool. We don&rsquo;t verify
            right-to-work, employment status, or the identity of anyone
            you invite into your account.
          </li>
          <li>A covert surveillance tool (see section 5).</li>
          <li>
            An authoritative record for disputes with HMRC, tribunals, or
            your clients.
          </li>
        </ul>
        <p>
          You&rsquo;re the employer and the business owner. Legal
          obligations to your staff, your clients, and the UK government
          rest with you.
        </p>
      </section>

      <section>
        <h2>3. Your account</h2>
        <ul>
          <li>
            You must be at least 18 years old and have authority to enter
            into these Terms on behalf of your business.
          </li>
          <li>
            Keep your password safe. You&rsquo;re responsible for what
            happens under your account.
          </li>
          <li>
            Tell us straight away if you think someone else has access:{" "}
            <a href="mailto:hello@tidyrota.com">hello@tidyrota.com</a>.
          </li>
          <li>One person per account. Don&rsquo;t share logins.</li>
        </ul>
      </section>

      <section>
        <h2>4. Pricing, trial, and cancellation</h2>

        <h3>Price</h3>
        <p>
          &pound;29 per month plus &pound;3.79 per month for each active
          cleaner on your team. Prices exclude VAT. We&rsquo;re not
          currently VAT-registered, so no VAT is charged.
        </p>

        <h3>Trial</h3>
        <p>
          New accounts come with a 14-day free trial, starting when you
          sign up. You don&rsquo;t need to enter card details to begin.
          If you don&rsquo;t subscribe before the trial ends your account
          is suspended. You&rsquo;ll still be able to reach your billing
          page so you can subscribe, until your data is deleted in line
          with the Privacy Policy.
        </p>

        <h3>Billing</h3>
        <p>
          Once subscribed, we bill monthly in advance through Stripe.
          Your bill is the base price plus &pound;3.79 per cleaner on
          your team when the invoice is generated. Mid-cycle changes to
          the number of cleaners are prorated by Stripe.
        </p>

        <h3>Cancelling</h3>
        <p>
          You can cancel at any time from your billing page. Your
          subscription stays active until the end of your paid period,
          then stops. You won&rsquo;t be charged again.
        </p>

        <h3>Refunds</h3>
        <p>
          We don&rsquo;t issue refunds for partial months or for periods
          you&rsquo;ve already been billed for. If you believe you&rsquo;ve
          been charged in error (a duplicate charge, or a bug stopped you
          using the service), email{" "}
          <a href="mailto:hello@tidyrota.com">hello@tidyrota.com</a> and
          we&rsquo;ll look into it.
        </p>

        <h3>Price changes</h3>
        <p>
          If we change our prices, we&rsquo;ll give you at least 30
          days&rsquo; notice by email. You can cancel before the new price
          takes effect.
        </p>
      </section>

      <section>
        <h2>5. Using TidyRota responsibly</h2>
        <p>You agree that you will not use TidyRota to:</p>
        <ul>
          <li>
            Break any law, including UK employment, data protection, or
            equality law.
          </li>
          <li>
            Track workers <strong>covertly</strong>. Cleaners must be told,
            in clear plain language, that their location is captured at
            check-in and what&rsquo;s done with it. This is both the right
            thing to do and a requirement under UK GDPR.
          </li>
          <li>
            Harass, bully, or otherwise mistreat members of your team.
          </li>
          <li>
            Enter personal data about people who haven&rsquo;t agreed to
            it sitting in your cleaning company&rsquo;s systems. For
            example, a client&rsquo;s personal mobile number they
            didn&rsquo;t give you for that purpose.
          </li>
          <li>
            Upload anything that is illegal, defamatory, obscene, or
            infringes someone else&rsquo;s rights.
          </li>
          <li>
            Try to break, disrupt, reverse-engineer, or misuse the service.
            For example: security probing without written permission,
            sending spam through our invite system, or scraping the app.
          </li>
          <li>
            Resell, sublicence, or pass off TidyRota as your own product.
          </li>
        </ul>
        <p>
          If you break these rules, we can suspend or close your account
          (section 12).
        </p>
      </section>

      <section>
        <h2>6. Your data and ours</h2>

        <h3>Your data</h3>
        <p>
          You own the data you put into TidyRota. You give us permission to
          store, copy, and process it for the purpose of running the
          service for you. We handle personal data in line with our{" "}
          <a href="/privacy">Privacy Policy</a>.
        </p>

        <h3>Data you put in about other people</h3>
        <p>
          When you invite cleaners, add clients, or schedule shifts,
          you&rsquo;re giving us personal data about other people. You
          confirm that you have a lawful reason to do so (typically
          employing the cleaner, or running your business). We process
          that data only on your instructions. Section 7 sets out our
          side.
        </p>

        <h3>Our service</h3>
        <p>
          We (and our suppliers) own the code, design, logos, and content
          that make up TidyRota. You don&rsquo;t get any ownership rights
          to TidyRota itself.
        </p>
      </section>

      <section>
        <h2>7. Data processing commitments</h2>
        <p>
          When you use TidyRota to store personal data about your staff or
          clients, the following terms apply. They form the data processing
          agreement between us.
        </p>
        <ul>
          <li>
            <strong>Purpose.</strong> We process that personal data only to
            deliver TidyRota to you and for no other purpose.
          </li>
          <li>
            <strong>Instructions.</strong> We act on your documented
            instructions, which are the settings you configure inside
            TidyRota and these Terms.
          </li>
          <li>
            <strong>Confidentiality.</strong> Our staff who access your
            data are bound by confidentiality.
          </li>
          <li>
            <strong>Security.</strong> We use appropriate technical and
            organisational measures (encryption in transit, password
            hashing, access controls, audit logging on billing changes).
          </li>
          <li>
            <strong>Sub-processors.</strong> We use the providers listed in
            the <a href="/privacy">Privacy Policy</a> (Stripe, Resend,
            Postcodes.io, DigitalOcean, Google Ads). We&rsquo;ll give at
            least 30 days&rsquo; notice of any new sub-processor by
            updating the Privacy Policy and emailing account owners.
          </li>
          <li>
            <strong>Sub-processor agreements.</strong> Each sub-processor
            is bound to similar data protection commitments.
          </li>
          <li>
            <strong>Your rights requests.</strong> If one of your cleaners
            or clients asks to see, correct, or delete data we hold on your
            behalf, we&rsquo;ll help you answer within a reasonable time.
          </li>
          <li>
            <strong>Breach notification.</strong> If we become aware of a
            personal data breach affecting your data, we&rsquo;ll tell you
            without undue delay.
          </li>
          <li>
            <strong>End of contract.</strong> When you leave, we&rsquo;ll
            delete or return the personal data in your account in line
            with the retention section of the Privacy Policy, unless the
            law requires us to keep it.
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Availability</h2>
        <p>
          We&rsquo;re a small team and we do our best to keep TidyRota
          running well, but we don&rsquo;t offer any availability
          guarantee. The service is provided{" "}
          <strong>&ldquo;as is&rdquo;</strong> and{" "}
          <strong>&ldquo;as available&rdquo;</strong>. From time to time
          we&rsquo;ll take it offline for maintenance, upgrades, or
          emergencies, and we&rsquo;ll give notice when we can.
        </p>
      </section>

      <section>
        <h2>9. Limitation of liability</h2>
        <p>Nothing in these Terms limits our liability for:</p>
        <ul>
          <li>Death or personal injury caused by our negligence.</li>
          <li>Fraud or fraudulent misrepresentation.</li>
          <li>Anything that can&rsquo;t be limited under UK law.</li>
        </ul>
        <p>
          Subject to that, and to the fullest extent allowed by law:
        </p>
        <ul>
          <li>
            We&rsquo;re not liable for indirect, consequential, or special
            losses. For example: lost profits, lost business, lost
            reputation, lost goodwill, fines from your clients, or HMRC
            penalties arising from how you used the check-in data.
          </li>
          <li>
            Our total liability to you for any claim, however caused, is
            capped at the fees you&rsquo;ve paid us in the 12 months before
            the event that caused the claim. If you&rsquo;ve paid us
            nothing (for example, during the free trial), our total
            liability is capped at &pound;100.
          </li>
        </ul>
        <p>
          You agree these limits are reasonable given what the service
          does and what we charge for it.
        </p>
      </section>

      <section>
        <h2>10. Indemnity</h2>
        <p>
          You agree to reimburse us for any loss we suffer as a result of:
        </p>
        <ul>
          <li>
            Something you put into TidyRota that you shouldn&rsquo;t have
            (see section 5).
          </li>
          <li>
            Your breach of UK employment, data protection, or equality law
            in how you use the service.
          </li>
          <li>
            A third party claiming you&rsquo;ve misused their personal data
            through TidyRota.
          </li>
        </ul>
      </section>

      <section>
        <h2>11. Changes</h2>

        <h3>To the service</h3>
        <p>
          We&rsquo;ll add, change, and remove features as the product
          evolves. If a change meaningfully reduces the core of what
          you&rsquo;re paying for, we&rsquo;ll give you notice and a
          chance to cancel first.
        </p>

        <h3>To these Terms</h3>
        <p>
          If we change these Terms, we&rsquo;ll email account owners and
          update the &ldquo;Last updated&rdquo; date at the top. If a
          change isn&rsquo;t in your favour, you can cancel before it
          takes effect.
        </p>
      </section>

      <section>
        <h2>12. Suspension and termination</h2>
        <p>You can cancel any time from your billing page.</p>
        <p>We can suspend or close your account if:</p>
        <ul>
          <li>You don&rsquo;t pay.</li>
          <li>You break these Terms, particularly section 5.</li>
          <li>
            Your account is involved in fraud, abuse, or a security
            incident and we need to protect other users.
          </li>
          <li>
            We&rsquo;re shutting the service down entirely. If that
            happens, we&rsquo;ll give as much notice as we can and help
            you export your data.
          </li>
        </ul>
        <p>
          Some clauses keep applying after your account ends: the ones
          covering intellectual property, limitation of liability, data
          handling (section 7), indemnity, and governing law.
        </p>
      </section>

      <section>
        <h2>13. Miscellaneous</h2>
        <ul>
          <li>
            <strong>No partnership.</strong> These Terms don&rsquo;t make
            us business partners or create an agency relationship.
          </li>
          <li>
            <strong>Assignment.</strong> You can&rsquo;t transfer these
            Terms without our agreement. We can transfer these Terms (for
            example, if we incorporate as a limited company or sell the
            business) as long as your rights aren&rsquo;t materially
            reduced.
          </li>
          <li>
            <strong>No waiver.</strong> If we don&rsquo;t enforce a right
            straight away, we haven&rsquo;t given it up.
          </li>
          <li>
            <strong>Severability.</strong> If a clause turns out to be
            unenforceable, the rest of the Terms still apply.
          </li>
          <li>
            <strong>Entire agreement.</strong> These Terms plus the
            Privacy Policy are the whole agreement between us about
            TidyRota.
          </li>
        </ul>
      </section>

      <section>
        <h2>14. Governing law and jurisdiction</h2>
        <p>
          These Terms are governed by the laws of England and Wales.
          Disputes will be decided by the courts of England and Wales.
        </p>
      </section>

      <section>
        <h2>15. Contact</h2>
        <p>
          Email:{" "}
          <a href="mailto:hello@tidyrota.com">hello@tidyrota.com</a>
          <br />
          Post: TidyRota, 152 Leeds Road, Wakefield, WF1 2QA, United
          Kingdom.
        </p>
      </section>
    </LegalPageShell>
  );
}
